import React, { useState, useEffect, useMemo } from 'react';
import { Command } from 'cmdk';
import {
  Search, CornerDownLeft, Clock, ArrowRight, RefreshCw, ShoppingBag, PackageOpen,
  FileText, LogOut, ScanBarcode, Phone, CalendarDays, Shirt, Loader2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { visibleGroups } from './navConfig';
import { detect } from '../api/lookup';
import { quickSyncSheets } from '../api/appointments';
import { syncNew } from '../api/orderHistory';
import { restockAll } from '../api/dispatch';

const RECENT_KEY = 'zocs_palette_recent';
const MAX_RECENT = 5;

function readRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}
function pushRecent(entry) {
  try {
    const id = `${entry.page}|${entry.data?.subTab || ''}|${entry.data?.query || entry.data?.search || ''}`;
    const next = [{ ...entry, id }, ...readRecent().filter(r => r.id !== id)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* storage blocked: recents just don't persist */ }
}

// What does the typed text look like? Drives the "smart" suggestions.
function classify(raw) {
  const q = raw.trim();
  if (!q) return null;
  const digits = q.replace(/[\s+-]/g, '');
  if (/^\d+$/.test(digits) && (digits.length === 10 || (digits.length === 12 && digits.startsWith('91')))) {
    return { kind: 'phone', value: digits.slice(-10) };
  }
  if (/^\d{3,9}$/.test(q)) return { kind: 'number', value: q };            // barcode or design ID
  if (/^[A-Za-z]{1,4}\s?\d{2,}[A-Za-z0-9-]*$/.test(q)) return { kind: 'uid', value: q.toUpperCase() };
  if (q.length >= 2) return { kind: 'text', value: q };
  return null;
}

function Item({ onSelect, icon: Icon, children, hint, value, forceMount }) {
  return (
    <Command.Item value={value} onSelect={onSelect} forceMount={forceMount}
      className="group flex items-center gap-3 px-3 h-10 mx-1.5 rounded-md text-[13px] text-gray-700 cursor-pointer
                 data-[selected=true]:bg-gray-100 data-[selected=true]:text-gray-900">
      {Icon && <Icon size={15} className="text-gray-400 group-data-[selected=true]:text-gray-700 shrink-0" />}
      <span className="flex-1 truncate">{children}</span>
      {hint && <span className="text-xs text-gray-400 truncate max-w-[45%]">{hint}</span>}
      <CornerDownLeft size={13} className="text-gray-300 opacity-0 group-data-[selected=true]:opacity-100" />
    </Command.Item>
  );
}

const groupClass = `[&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1.5
  [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase
  [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-gray-400`;

export default function CommandPalette({ open, onOpenChange, onNavigate, notify }) {
  const { auth, logout } = useAuth();
  const isAdmin = auth?.access_level === 'admin';
  const isCore = isAdmin || auth?.access_level === 'core_team';
  const [search, setSearch] = useState('');
  const [recent, setRecent] = useState([]);
  const [detected, setDetected] = useState(null);   // admin-only /lookup/detect result
  const [detecting, setDetecting] = useState(false);

  const groups = useMemo(() => visibleGroups(auth), [auth]);
  const smart = classify(search);

  useEffect(() => {
    if (open) { setSearch(''); setDetected(null); setRecent(readRecent()); }
  }, [open]);

  // Admins get a live "what is this?" answer from the Lookup tool's detector.
  useEffect(() => {
    setDetected(null);
    if (!isAdmin || !smart || smart.kind === 'text') return;
    let cancelled = false;
    setDetecting(true);
    const t = setTimeout(async () => {
      try {
        const r = await detect(smart.value);
        if (!cancelled) setDetected(r);
      } catch { /* detect is a nice-to-have; the plain suggestions still work */ }
      finally { if (!cancelled) setDetecting(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); setDetecting(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, isAdmin]);

  function go(page, data = {}, label) {
    pushRecent({ page, data, label });
    onOpenChange(false);
    onNavigate(page, data);
  }

  async function runAction(label, fn, format) {
    onOpenChange(false);
    notify(`${label}…`);
    try {
      const res = await fn();
      notify(format ? format(res) : `${label} done`);
    } catch (e) {
      notify(e.response?.data?.detail || `${label} failed`, 'error');
    }
  }

  // Smart suggestions built from the typed text.
  const smartItems = [];
  if (smart) {
    const v = smart.value;
    if (smart.kind === 'number') {
      smartItems.push({ key: 'ih', icon: ScanBarcode, label: `Item history for barcode ${v}`, run: () => go('itemhistory', { query: v }, `Item history · ${v}`) });
      if (isAdmin) smartItems.push({ key: 'lk', icon: Shirt, label: `Look up ${v} (barcode or design)`, run: () => go('lookup', { query: v }, `Lookup · ${v}`) });
    }
    if (smart.kind === 'phone') {
      smartItems.push({ key: 'ap', icon: Phone, label: `Appointments for ${v}`, run: () => go('appointments', { search: v }, `Appointments · ${v}`) });
      if (isAdmin) smartItems.push({ key: 'lk', icon: Search, label: `Customer lookup for ${v}`, run: () => go('lookup', { query: v }, `Lookup · ${v}`) });
    }
    if (smart.kind === 'uid' && isAdmin) {
      smartItems.push({ key: 'lk', icon: CalendarDays, label: `Look up appointment ${v}`, run: () => go('lookup', { query: v }, `Lookup · ${v}`) });
    }
    if (smart.kind === 'text') {
      smartItems.push({ key: 'ap', icon: CalendarDays, label: `Search appointments for "${v}"`, run: () => go('appointments', { search: v }, `Appointments · ${v}`) });
    }
  }

  // Identifiers (barcode/phone/UID) lead with the smart rows; plain words lead with page matches.
  const smartFirst = smart && smart.kind !== 'text';

  // Describe what /lookup/detect found, so the admin knows before pressing Enter.
  let detectedHint = null;
  if (detected?.type === 'not_found') detectedHint = 'Nothing found for this';
  else if (detected?.type === 'ambiguous') detectedHint = detected.candidates.map(c => c.label).join(' · ');
  else if (detected?.resolved) {
    const r = detected.resolved;
    detectedHint = r.customer_name || r.design_name || r.label || null;
  }

  const actions = [
    isCore && { key: 'qs', icon: RefreshCw, label: 'Quick Sync appointments', run: () => runAction('Quick Sync', quickSyncSheets, r => r.message || 'Quick Sync done') },
    isAdmin && { key: 'sn', icon: ShoppingBag, label: 'Sync new orders', run: () => runAction('Sync new orders', syncNew, r => {
      const s = r.sync || r; const inv = r.inventory || {};
      return `Orders synced: ${s.orders_created || 0} new, ${s.rto_dol_created || 0} RTO/DOL. ${inv.marked_sold || 0} items marked sold.`;
    }) },
    isAdmin && { key: 'ra', icon: PackageOpen, label: 'Restock all reversed items', run: () => {
      if (!window.confirm('Send every item in the Restock queue to its suggested bin?')) return;
      runAction('Restock all', restockAll, r => `Restocked ${r.restocked} item(s)` + (r.skipped ? `, ${r.skipped} skipped (no bin)` : ''));
    } },
    isCore && { key: 'gi', icon: FileText, label: 'Generate invoice', run: () => go('orderops', { subTab: 'receipt' }, 'Order Ops › Generate Invoice') },
    { key: 'so', icon: LogOut, label: 'Sign out', run: () => { onOpenChange(false); logout(); } },
  ].filter(Boolean);

  return (
    <Command.Dialog open={open} onOpenChange={onOpenChange} label="Command menu" loop
      overlayClassName="fixed inset-0 z-50 bg-black/20 backdrop-blur-[1px]"
      contentClassName="fixed z-50 left-1/2 top-[12vh] -translate-x-1/2 w-[calc(100vw-2rem)] max-w-[620px]
                        bg-white rounded-xl border border-gray-200 shadow-2xl overflow-hidden focus:outline-none">
      <div className="flex items-center gap-2.5 px-4 border-b border-gray-200">
        <Search size={16} className="text-gray-400 shrink-0" />
        <Command.Input value={search} onValueChange={setSearch} autoFocus
          placeholder="Search pages, barcodes, phones, UIDs, actions…"
          className="flex-1 h-12 bg-transparent text-[14px] text-gray-900 placeholder:text-gray-400 outline-none border-0 focus:ring-0 p-0" />
        {detecting && <Loader2 size={14} className="text-gray-400 animate-spin" />}
        <kbd className="text-[11px] text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">esc</kbd>
      </div>

      <Command.List className="max-h-[min(60vh,440px)] overflow-y-auto py-1.5 scroll-py-2">
        {smartItems.length === 0 && (
          <Command.Empty className="py-10 text-center text-[13px] text-gray-400">No matches.</Command.Empty>
        )}

        {smartFirst && smartItems.length > 0 && (
          <Command.Group heading="Search" forceMount className={groupClass}>
            {smartItems.map((s, i) => (
              <Item key={s.key} value={`smart-${s.key}`} forceMount icon={s.icon} onSelect={s.run}
                hint={i === 0 || s.key === 'lk' ? detectedHint : null}>
                {s.label}
              </Item>
            ))}
          </Command.Group>
        )}

        {!search && recent.length > 0 && (
          <Command.Group heading="Recent" className={groupClass}>
            {recent.map(r => (
              <Item key={r.id} value={`recent ${r.label}`} icon={Clock} onSelect={() => go(r.page, r.data, r.label)}>
                {r.label}
              </Item>
            ))}
          </Command.Group>
        )}

        <Command.Group heading="Go to" className={groupClass}>
          {groups.flatMap(g => g.items.flatMap(item => [
            <Item key={item.key} value={`${item.label} ${g.label}`} icon={item.icon}
              onSelect={() => go(item.key, {}, item.label)} hint={g.label}>
              {item.label}
            </Item>,
            ...item.subTabs.map(t => (
              <Item key={`${item.key}.${t.key}`} value={`${item.label} ${t.label}`} icon={ArrowRight}
                onSelect={() => go(item.key, { subTab: t.key }, `${item.label} › ${t.label}`)}>
                <span className="text-gray-400">{item.label} ›</span> {t.label}
              </Item>
            )),
          ]))}
        </Command.Group>

        {!smartFirst && smartItems.length > 0 && (
          <Command.Group heading="Search" forceMount className={groupClass}>
            {smartItems.map((s, i) => (
              <Item key={s.key} value={`smart-${s.key}`} forceMount icon={s.icon} onSelect={s.run}
                hint={i === 0 || s.key === 'lk' ? detectedHint : null}>
                {s.label}
              </Item>
            ))}
          </Command.Group>
        )}

        <Command.Group heading="Actions" className={groupClass}>
          {actions.map(a => (
            <Item key={a.key} value={`action ${a.label}`} icon={a.icon} onSelect={a.run}>{a.label}</Item>
          ))}
        </Command.Group>
      </Command.List>

      <div className="flex items-center gap-4 px-4 h-9 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-400">
        <span><kbd className="font-sans">↑↓</kbd> navigate</span>
        <span><kbd className="font-sans">↵</kbd> open</span>
        <span className="ml-auto">Tip: type a barcode, phone or UID</span>
      </div>
    </Command.Dialog>
  );
}
